import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type { Carrier, Trunk, RateDeck, RateRule, BillingAccount, Movement } from '@arcnet/contracts';

interface ListResponse<T> { data: T[] }

export const billingKeys = {
  carriers: () => ['carriers'] as const,
  trunks: (carrierId?: string) => ['trunks', carrierId ?? 'all'] as const,
  decks: () => ['rate-decks'] as const,
  deckRules: (deckId: string, q?: string) => ['rate-deck-rules', deckId, q ?? ''] as const,
  billing: (tenantId: string) => ['billing', tenantId] as const,
  movements: (tenantId: string) => ['billing-movements', tenantId] as const,
};

export const useCarriers = () =>
  useQuery({
    queryKey: billingKeys.carriers(),
    queryFn: () => api.get<ListResponse<Carrier>>('/v1/carriers'),
  });

export const useTrunks = (carrierId?: string) =>
  useQuery({
    queryKey: billingKeys.trunks(carrierId),
    queryFn: () =>
      api.get<ListResponse<Trunk>>(`/v1/trunks${carrierId ? `?carrier_id=${carrierId}` : ''}`),
    // Carries the switch's live view of each trunk (health, registration),
    // which the agent refreshes every 10 s. Cheap: a handful of rows.
    refetchInterval: 15_000,
  });

export const useRateDecks = () =>
  useQuery({
    queryKey: billingKeys.decks(),
    queryFn: () => api.get<ListResponse<RateDeck>>('/v1/rate-decks'),
  });

export const useRateRules = (deckId: string | null, q: string) =>
  useQuery({
    queryKey: billingKeys.deckRules(deckId ?? '', q),
    queryFn: () =>
      api.get<{ data: RateRule[]; total: number; version: number; is_published: boolean }>(
        `/v1/rate-decks/${deckId}/rules${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ),
    enabled: !!deckId,
  });

export interface RateTestResult {
  ok: boolean;
  charge: string | null;
  currency: string | null;
  billable_seconds: number | null;
  reason: string | null;
  deck_version?: number;
  breakdown: {
    matched_prefix: string; rule_id: string; band: string;
    raw_seconds: number; free_seconds: number; billable_seconds: number;
    rate_per_minute: string; connection_fee: string; minimum_charge_applied: boolean;
  } | null;
}

/**
 * The rate lookup tester. Not cached: the whole point is to answer "what would
 * THIS call cost right now", and a stale answer during a billing dispute is
 * worse than no answer.
 */
export const useRateTest = (deckId: string | null) =>
  useMutation({
    mutationFn: (body: { destination: string; duration_sec: number }) =>
      api.post<RateTestResult>(`/v1/rate-decks/${deckId}/actions/test`, body),
  });

export const useBillingAccount = (tenantId: string | null) =>
  useQuery({
    queryKey: billingKeys.billing(tenantId ?? ''),
    queryFn: () => api.get<BillingAccount>(`/v1/tenants/${tenantId}/billing`),
    enabled: !!tenantId,
    retry: false, // a tenant with no billing account 404s; that is a real answer
  });

export const useMovements = (tenantId: string | null) =>
  useQuery({
    queryKey: billingKeys.movements(tenantId ?? ''),
    queryFn: () => api.get<ListResponse<Movement>>(`/v1/tenants/${tenantId}/billing/movements`),
    enabled: !!tenantId,
  });

export function useAdjustBalance(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { amount: string; kind: string; reason: string }) =>
      api.post<{ balance: string }>(`/v1/tenants/${tenantId}/billing/actions/adjust`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billingKeys.billing(tenantId) });
      void qc.invalidateQueries({ queryKey: billingKeys.movements(tenantId) });
    },
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/v1/tenants', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tenants'] }); },
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch(`/v1/tenants/${id}`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tenants'] }); },
  });
}
